import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Refresh,
  Message,
  Person,
  Schedule,
  TrendingUp,
  WhatsApp,
  Phone,
  Group,
  Forward,
  Visibility,
} from "@mui/icons-material";

interface WhatsAppMessage {
  message_id: string;
  from_number: string;
  to_number: string;
  message_body: string;
  message_type: string;
  timestamp: string;
  received_at: string;
  processed: boolean;
  sender_name: string;
  is_group_message: boolean;
  forwarded: boolean;
  media_filename?: string;
  media_size?: number;
  media_url?: string;
  group_id?: string;
  group_name?: string;
  reply_to_message_id?: string;
  sender_profile_pic?: string;
}

interface MessageStats {
  total_messages: number;
  unique_contacts: number;
  group_messages: number;
  forwarded_messages: number;
  media_messages: number;
  recent_activity: number;
}

const WhatsAppMonitorDashboard: React.FC = () => {
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [stats, setStats] = useState<MessageStats>({
    total_messages: 0,
    unique_contacts: 0,
    group_messages: 0,
    forwarded_messages: 0,
    media_messages: 0,
    recent_activity: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterType, setFilterType] = useState<string>("all");
  const [monitoredNumber] = useState("+15613795657");

  const fetchMessages = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);

      // Get the auth token from localStorage
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication required. Please log in again.");
        return;
      }

      // Call the backend API to get messages using authenticated endpoint
      const response = await fetch(
        `/api/whatsapp-monitor/messages?to_number=${encodeURIComponent(
          monitoredNumber
        )}&limit=100`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const messages = await response.json();
        setMessages(messages || []);
        calculateStats(messages || []);
      } else if (response.status === 401) {
        setError("Authentication required. Please log in again.");
        // Clear invalid token
        localStorage.removeItem("token");
      } else {
        setError(`Failed to fetch messages: ${response.statusText}`);
      }
    } catch (err) {
      setError(
        `Error fetching messages: ${
          err instanceof Error ? err.message : "Unknown error"
        }`
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [monitoredNumber]);

  const calculateStats = (messageList: WhatsAppMessage[]) => {
    const uniqueContacts = new Set(messageList.map((msg) => msg.from_number));
    const groupMessages = messageList.filter(
      (msg) => msg.is_group_message
    ).length;
    const forwardedMessages = messageList.filter((msg) => msg.forwarded).length;
    const mediaMessages = messageList.filter(
      (msg) => msg.message_type !== "text"
    ).length;

    // Calculate recent activity (messages in last 24 hours)
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const recentActivity = messageList.filter((msg) => {
      const msgTime = new Date(msg.timestamp);
      return msgTime > yesterday;
    }).length;

    setStats({
      total_messages: messageList.length,
      unique_contacts: uniqueContacts.size,
      group_messages: groupMessages,
      forwarded_messages: forwardedMessages,
      media_messages: mediaMessages,
      recent_activity: recentActivity,
    });
  };

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleRefresh = () => {
    fetchMessages();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredMessages = messages.filter((message) => {
    if (filterType === "all") return true;
    if (filterType === "group") return message.is_group_message;
    if (filterType === "forwarded") return message.forwarded;
    if (filterType === "media") return message.message_type !== "text";
    return true;
  });

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const truncateMessage = (message: string, maxLength: number = 50) => {
    return message.length > maxLength
      ? `${message.substring(0, maxLength)}...`
      : message;
  };

  const getMessageTypeIcon = (messageType: string) => {
    switch (messageType) {
      case "image":
        return "🖼️";
      case "video":
        return "🎥";
      case "audio":
        return "🎵";
      case "document":
        return "📄";
      case "sticker":
        return "😀";
      default:
        return "💬";
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" gutterBottom>
          📱 WhatsApp Message Monitor
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Filter"
            >
              <MenuItem value="all">All Messages</MenuItem>
              <MenuItem value="group">Group Messages</MenuItem>
              <MenuItem value="forwarded">Forwarded</MenuItem>
              <MenuItem value="media">Media Messages</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh Messages">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Message color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.total_messages}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Messages
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Person color="secondary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.unique_contacts}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique Contacts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Group color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.group_messages}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Group Messages
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Forward color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">
                    {stats.forwarded_messages}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Forwarded
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <WhatsApp color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.media_messages}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Media Messages
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TrendingUp color="error" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.recent_activity}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last 24h
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Messages Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="h6">
            Messages for {monitoredNumber}
            {refreshing && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Sender</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredMessages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No messages found
                  </TableCell>
                </TableRow>
              ) : (
                filteredMessages
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((message) => (
                    <TableRow key={message.message_id} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Schedule sx={{ mr: 1, fontSize: 16 }} />
                          {formatTimestamp(message.timestamp)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Phone sx={{ mr: 1, fontSize: 16 }} />
                          {message.from_number}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            sx={{ width: 24, height: 24, mr: 1, fontSize: 12 }}
                          >
                            {message.sender_name?.charAt(0) || "?"}
                          </Avatar>
                          {message.sender_name || "Unknown"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={
                            <span>
                              {getMessageTypeIcon(message.message_type)}
                            </span>
                          }
                          label={message.message_type}
                          size="small"
                          color={
                            message.message_type === "text"
                              ? "default"
                              : "primary"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={message.message_body}>
                          <Typography variant="body2">
                            {truncateMessage(message.message_body)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {message.is_group_message && (
                            <Chip label="Group" size="small" color="info" />
                          )}
                          {message.forwarded && (
                            <Chip
                              label="Forwarded"
                              size="small"
                              color="warning"
                            />
                          )}
                          {message.processed && (
                            <Chip
                              label="Processed"
                              size="small"
                              color="success"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredMessages.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Recent Contacts */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Recent Contacts
        </Typography>
        <List dense>
          {messages
            .reduce((acc, message) => {
              const existing = acc.find(
                (item) => item.from_number === message.from_number
              );
              if (!existing) {
                acc.push({
                  from_number: message.from_number,
                  sender_name: message.sender_name,
                  last_message: message.message_body,
                  last_time: message.timestamp,
                  message_count: 1,
                });
              } else {
                existing.message_count++;
                if (
                  new Date(message.timestamp) > new Date(existing.last_time)
                ) {
                  existing.last_message = message.message_body;
                  existing.last_time = message.timestamp;
                }
              }
              return acc;
            }, [] as any[])
            .sort(
              (a, b) =>
                new Date(b.last_time).getTime() -
                new Date(a.last_time).getTime()
            )
            .slice(0, 5)
            .map((contact, index) => (
              <React.Fragment key={contact.from_number}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>{contact.sender_name?.charAt(0) || "?"}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${contact.sender_name || "Unknown"} (${
                      contact.from_number
                    })`}
                    secondary={`${
                      contact.message_count
                    } messages • ${truncateMessage(contact.last_message)}`}
                  />
                </ListItem>
                {index < 4 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
        </List>
      </Paper>
    </Container>
  );
};

export default WhatsAppMonitorDashboard;
